import type {
  CreateProcessTobeInput,
  CreateTobeActivityInput,
  DeleteProcessTobeResult,
  ProcessTobe,
  TobeActivity,
  TobeActivityWithProcess,
  TobeEditLog,
  TobeProcessDetail,
  UpdateProcessTobeInput,
  UpdateTobeActivityInput,
} from '@internal/orquestra-contract';
import { OrquestraProcessAsIsClient } from './OrquestraProcessAsIsClient';

function base(projectId: number): string {
  return `/projects/${projectId}/processes-to-be`;
}

function actBase(projectId: number, processId: number): string {
  return `${base(projectId)}/${processId}/activities`;
}

/**
 * Client do Orquestra com os métodos do Process TO-BE (Fase 4.2). Estende
 * `OrquestraProcessAsIsClient`; o client concreto registrado no `apiFactory`
 * (que implementa o `OrquestraApi` completo) é o `OrquestraImagineClient`, que
 * estende esta classe — split para manter cada arquivo sob 300 linhas.
 */
export class OrquestraProcessToBeClient extends OrquestraProcessAsIsClient {
  async listProcessesTobe(projectId: number): Promise<ProcessTobe[]> {
    return this.request<ProcessTobe[]>(base(projectId));
  }

  async getProcessTobeDetail(
    projectId: number,
    processId: number,
  ): Promise<TobeProcessDetail> {
    return this.request<TobeProcessDetail>(`${base(projectId)}/${processId}`);
  }

  async createProcessTobe(
    projectId: number,
    input: CreateProcessTobeInput,
  ): Promise<ProcessTobe> {
    return this.request<ProcessTobe>(
      base(projectId),
      this.jsonInit('POST', input),
    );
  }

  async updateProcessTobe(
    projectId: number,
    processId: number,
    input: UpdateProcessTobeInput,
  ): Promise<ProcessTobe> {
    return this.request<ProcessTobe>(
      `${base(projectId)}/${processId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteProcessTobe(
    projectId: number,
    processId: number,
    justification: string,
  ): Promise<DeleteProcessTobeResult> {
    return this.request<DeleteProcessTobeResult>(
      `${base(projectId)}/${processId}`,
      this.jsonInit('DELETE', { justification }),
    );
  }

  async reorderProcessesTobe(
    projectId: number,
    orderedIds: number[],
  ): Promise<ProcessTobe[]> {
    return this.request<ProcessTobe[]>(
      `${base(projectId)}/reorder`,
      this.jsonInit('PUT', { orderedIds }),
    );
  }

  async countLinkedSolucoes(
    projectId: number,
    processId: number,
  ): Promise<{ count: number }> {
    return this.request<{ count: number }>(
      `${base(projectId)}/${processId}/linked-solucoes-count`,
    );
  }

  async listTobeActivities(
    projectId: number,
    processId: number,
  ): Promise<TobeActivity[]> {
    return this.request<TobeActivity[]>(actBase(projectId, processId));
  }

  async listAllTobeActivities(
    projectId: number,
  ): Promise<TobeActivityWithProcess[]> {
    return this.request<TobeActivityWithProcess[]>(
      `/projects/${projectId}/tobe-activities`,
    );
  }

  async createTobeActivity(
    projectId: number,
    processId: number,
    input: CreateTobeActivityInput,
  ): Promise<TobeActivity> {
    return this.request<TobeActivity>(
      actBase(projectId, processId),
      this.jsonInit('POST', input),
    );
  }

  async updateTobeActivity(
    projectId: number,
    processId: number,
    activityId: number,
    input: UpdateTobeActivityInput,
  ): Promise<TobeActivity> {
    return this.request<TobeActivity>(
      `${actBase(projectId, processId)}/${activityId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteTobeActivity(
    projectId: number,
    processId: number,
    activityId: number,
    justification = '',
  ): Promise<void> {
    await this.request(
      `${actBase(projectId, processId)}/${activityId}`,
      this.jsonInit('DELETE', { justification }),
    );
  }

  async reorderTobeActivities(
    projectId: number,
    processId: number,
    activityIds: number[],
  ): Promise<TobeActivity[]> {
    return this.request<TobeActivity[]>(
      `${actBase(projectId, processId)}/reorder`,
      this.jsonInit('PUT', { activityIds }),
    );
  }

  async getProcessTobeEditLog(
    projectId: number,
    processId: number,
  ): Promise<TobeEditLog[]> {
    return this.request<TobeEditLog[]>(
      `${base(projectId)}/${processId}/edit-log`,
    );
  }

  async getTobeActivityEditLog(
    projectId: number,
    processId: number,
    activityId: number,
  ): Promise<TobeEditLog[]> {
    return this.request<TobeEditLog[]>(
      `${actBase(projectId, processId)}/${activityId}/edit-log`,
    );
  }
}
